import Konva from 'konva';
import { Circle, Group, Line, Rect } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
	canStartDragFromNode,
	circleToPolygon,
	detectShape,
	flatToNested,
	getHandleHitFunc,
	getCircleHandlePoints,
	getCircleFromPolygon,
	getObstructionHandleSize,
	isPolygonSelfIntersecting,
	OBSTRUCTION_HANDLE_HIT_STROKE,
	OBSTRUCTION_DEFAULT_FILL,
	OBSTRUCTION_INVALID_FILL,
	pointsToBounds,
	polygonArea,
	RectBounds,
	updatePolygonPoint,
} from './tool/Editor/AddObstructionBtn/utils';
import { setEditedObstruction, setInvalidObstructionState, setActiveDragFunctionality, setMapOnlyFrozenPoint } from 'store/slices/ToolSlice';
import { pushUndoAction } from 'store/slices/ObstructionUndoSlice';
import { RootState } from 'store';
import { OBSTRUCTION_HANDLE_SIZE_CONFIG } from '../constants';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { useRefs } from 'contexts/RefContext';
import { OBS_EDIT_POINT_ACTION } from 'store/slices/ToolSlice/types';
import { addNewPoint, distanceToEdge } from './tool/Facet/utils';
import { appToast } from 'components/Basic/CustomToast';
import InvalidFacetEditToast from 'components/tool/Group/InvalidModal';

const HANDLE_CIRCLE_RADIUS = 0.25;
const OBSTRUCTION_DELETE_POINT_HIT_SIZE = 18;
const OBSTRUCTION_DELETE_POINT_HIT_SIZE_TOUCH = 24;
const OBSTRUCTION_DELETE_POINT_CLICK_THRESHOLD = 4;
const OBSTRUCTION_ADD_POINT_CLICK_THRESHOLD = 10;
const OBSTRUCTION_ADD_POINT_INNER_THRESHOLD = 2;
const OBSTRUCTION_ADD_POINT_HIT_STROKE = 28;
// ============  EDITING ZONE THRESHOLD ============
const EDITING_ZONE_THRESHOLD = 20; 

// Handle colors when NOT actively editable/selected
const INACTIVE_HANDLE = {
	fill: '#FFFFFF',
	stroke: 'rgb(255 38 0 / 50%)',
};

// Handle colors when actively editable/selected
const ACTIVE_HANDLE = {
	fill: 'rgb(255 38 0 / 50%)',
	stroke: '#FFFFFF',
};
const ADDED_POINTS_HIGHLIGHT = {
	fill: '#FFC107',
	stroke: '#FFFFFF',
};

const toPointKey = (x: number, y: number) => `${x.toFixed(6)},${y.toFixed(6)}`;

const DRAG_FUNCTIONALITY = 'editObstructionPoint';
const RAW_OBSTRUCTION_BODY_NODE_NAMES = ['raw-obs-rect', 'raw-obs-circle', 'raw-obs-line'];
const RAW_OBSTRUCTION_DRAG_BLOCKER_NODE_NAMES = ['raw-obs-edit-zone', 'raw-obs-handle'];

function buildSquareHandlesRectangle(
	id: string, // Added ID here for native Konva targeting
	points: number[],
	onDragStart: (index: number) => void,
	onDragMove: (index: number, x: number, y: number) => void,
	onDragEnd: () => void,
	isDraggingRef: React.MutableRefObject<boolean>,
	size: number,
	strokeWidth: number,
	hitStrokeWidth: number,
	hitPadding: number,
	interactive = false,
	onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void,
	onTap?: (e: Konva.KonvaEventObject<Event>) => void,
	onHandleEnter?: () => void,
	onHandleLeave?: () => void,
) {
	const handleIndices = [0, 1, 2, 3];

	return handleIndices.map((i) => {
		const cx = points[i * 2];
		const cy = points[i * 2 + 1];

		return (
			<Rect
				key={i}
				id={id}
				x={cx - size / 2}
				y={cy - size / 2}
				width={size}
				height={size}
				draggable={interactive}
				name="raw-obs-handle"
				fill={interactive ? ACTIVE_HANDLE.fill : INACTIVE_HANDLE.fill}
				stroke={interactive ? ACTIVE_HANDLE.stroke : INACTIVE_HANDLE.stroke}
				strokeWidth={strokeWidth}
				strokeScaleEnabled={false}
				hitStrokeWidth={hitStrokeWidth}
				hitFunc={getHandleHitFunc('square', size, hitPadding)}
				onMouseEnter={onHandleEnter}
				onMouseLeave={onHandleLeave}
				onClick={onClick}
				onTap={onTap}    
				onDragStart={interactive ? (e) => {
					isDraggingRef.current = true;
					onDragStart(i);
					e.cancelBubble = true;
					e.evt?.preventDefault();
				} : undefined}
				onDragMove={interactive ? (e) => {
					const x = e.target.x() + size / 2;
					const y = e.target.y() + size / 2;
					onDragMove(i, x, y);
					e.cancelBubble = true;
				} : undefined}
				onDragEnd={interactive ? (e) => {
					onDragEnd();
					isDraggingRef.current = false;
					e.cancelBubble = true;
					e.evt?.preventDefault();
				} : undefined}
			/>
		);
	});
}

function buildCircleHandlesForPolygon(
	id: string,
	points: number[],
	indices: number[],
	onDrag: (i: number, x: number, y: number) => void,
	onDragEnd: () => void,
	captureBeforeSnapshot: () => void,
	isDraggingRef: React.MutableRefObject<boolean>,
	radius: number,
	strokeWidth: number,
	hitStrokeWidth: number,
	hitPadding: number,
	interactive = false,
	highlightedKeys?: Set<string>,
	onPointClick?: (e: Konva.KonvaEventObject<MouseEvent>, index: number) => void,
	onPointTap?: (e: Konva.KonvaEventObject<Event>, index: number) => void,
	onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void,
	onTap?: (e: Konva.KonvaEventObject<Event>) => void,
	onHandleEnter?: () => void,
	onHandleLeave?: () => void,
) {
	return indices.map((idx) => {
		const key = toPointKey(points[idx], points[idx + 1]);
		const isHighlighted = highlightedKeys?.has(key) ?? false;
		const fill = interactive
			? (isHighlighted ? ADDED_POINTS_HIGHLIGHT.fill : ACTIVE_HANDLE.fill)
			: INACTIVE_HANDLE.fill;
		const stroke = interactive
			? (isHighlighted ? ADDED_POINTS_HIGHLIGHT.stroke : ACTIVE_HANDLE.stroke)
			: INACTIVE_HANDLE.stroke;

		return (
			<Circle
				key={idx}
				id={id}
				x={points[idx]}
				y={points[idx + 1]}
				radius={radius}
				draggable={interactive}
				name="raw-obs-handle"
				stroke={stroke}
				fill={fill}
				strokeWidth={strokeWidth}
				strokeScaleEnabled={false}
				hitStrokeWidth={hitStrokeWidth}
				hitFunc={getHandleHitFunc('circle', radius, hitPadding)}
				onMouseEnter={onHandleEnter}
				onMouseLeave={onHandleLeave}
				onClick={onPointClick ? (e) => onPointClick(e, idx) : onClick}
				onTap={onPointTap ? (e) => onPointTap(e, idx) : onTap}
				onDragStart={interactive ? (e) => {
					isDraggingRef.current = true;
					captureBeforeSnapshot();
					e.cancelBubble = true;
					e.evt?.preventDefault();
				} : undefined}
				onDragMove={interactive ? (e) => onDrag(idx, e.target.x(), e.target.y()) : undefined}
				onDragEnd={interactive ? (e) => {
					onDragEnd();
					isDraggingRef.current = false;
					e.cancelBubble = true;
					e.evt?.preventDefault();
				} : undefined}
			/>
		);
	});
}

function buildSquareHandlesForCircle(
	id: string,
	center: { x: number; y: number },
	radius: number,
	onDrag: (x: number, y: number) => void,
	onDragEnd: () => void,
	captureBeforeSnapshot: () => void,
	isDraggingRef: React.MutableRefObject<boolean>,
	size: number,
	strokeWidth: number,
	hitStrokeWidth: number,
	hitPadding: number,
	interactive = false,
	onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void,
	onTap?: (e: Konva.KonvaEventObject<Event>) => void,
	onHandleEnter?: () => void,
	onHandleLeave?: () => void,
) {
	return getCircleHandlePoints(center.x, center.y, radius).map((p, i) => {
		const isHorizontal = i === 0 || i === 2; // 0°, 180°
		const isVertical = i === 1 || i === 3; // 90°, 270°

		return (
			<Rect
				key={i}
				id={id} 
				x={p.x - size / 2}
				y={p.y - size / 2}
				width={size}
				height={size}
				draggable={interactive}
				name="raw-obs-handle"
				fill={interactive ? ACTIVE_HANDLE.fill : INACTIVE_HANDLE.fill}
				stroke={interactive ? ACTIVE_HANDLE.stroke : INACTIVE_HANDLE.stroke}
				strokeWidth={strokeWidth}
				strokeScaleEnabled={false}
				hitStrokeWidth={hitStrokeWidth}
				hitFunc={getHandleHitFunc('square', size, hitPadding)}
				onMouseEnter={onHandleEnter}
				onMouseLeave={onHandleLeave}
				onClick={onClick}
				onTap={onTap}
				onDragStart={interactive ? (e) => {
					isDraggingRef.current = true;
					captureBeforeSnapshot();
					e.cancelBubble = true;
					e.evt?.preventDefault();
				} : undefined}
				onDragMove={interactive ? (e) => {
					let hx = e.target.x() + size / 2;
					let hy = e.target.y() + size / 2;
					// Lock orthogonal axis
					if (isHorizontal) {
						hy = center.y;
						e.target.y(center.y - size / 2);
					}
					if (isVertical) {
						hx = center.x;
						e.target.x(center.x - size / 2);
					}
					onDrag(hx, hy);
					e.cancelBubble = true;
				} : undefined}
				onDragEnd={interactive ? (e) => {
					onDragEnd();
					isDraggingRef.current = false;
					e.cancelBubble = true;
					e.evt?.preventDefault();
				} : undefined}
			/>
		);
	});
}

export function EditableRawObstruction({
	id,
	points,
	assessment = 'RAW',
	userMapped = true,
	readOnly = true,
	interactive = false,
	pointsHighlightCacheRef,
	onClick,
	onTap,
	onMouseEnter,
	onMouseLeave,
}: {
	id: string;
	points: number[];
	assessment?: 'RAW' | 'CIRCLE' | 'RAW_LARGE';
	userMapped?: boolean;
	readOnly?: boolean;
	interactive?: boolean;
	pointsHighlightCacheRef?: React.MutableRefObject<Record<string, { highlighted: Set<string>; removed: Set<string> }>>;
	onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
	onTap?: (e: Konva.KonvaEventObject<Event>) => void;
	onMouseEnter?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
	onMouseLeave?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
}) {
	const dispatch = useDispatch();
	const { zoomLevel, fullScreenEnabled, obstructionEditPointAction, invalidObstructionIds, deleteObstructionsMode,
		hideSatelliteOverlay, mapOnlyFrozenPoint } =
		useSelector((state: RootState) => state.toolNewPostions.data);
	const { imgHeight } = useSelector((state: RootState) => state.roofData.data);
	const scale = useKonvaImageScale();
	const { konvaRef } = useRefs();
	const isCircle = assessment === 'CIRCLE';
	const fallbackCursor = interactive ? 'default' : 'crosshair';
	const pointsRef = useRef<number[]>(points.slice());
	const prevPointsRef = useRef<number[]>(points.slice());
	const circleRef = useRef(isCircle ? getCircleFromPolygon(pointsRef.current) : null);
	const shape = useMemo(() => detectShape(pointsRef.current), []);
	const rectBoundsRef = useRef<RectBounds | null>(
		shape === 'RECTANGLE' ? pointsToBounds(pointsRef.current) : null
	);
	const rectDragIndexRef = useRef<number | null>(null);
	const originalSnapshotRef = useRef<Obstruction | null>(null);
	const hasPushedUndoRef = useRef(false);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const getCacheEntry = () => {
		if (!pointsHighlightCacheRef) return null;
		const entry = pointsHighlightCacheRef.current[id] ?? {
			highlighted: new Set<string>(),
			removed: new Set<string>(),
		};
		if (!pointsHighlightCacheRef.current[id]) {
			pointsHighlightCacheRef.current[id] = entry;
		}
		return entry;
	};

	const [highlightedPointKeys, setHighlightedPointKeys] = useState<Set<string>>(
		() => new Set(getCacheEntry()?.highlighted ?? [])
	);
	const isDraggingRef = useRef(false);
	const removedHighlightedKeysRef = useRef<Set<string>>(
		new Set(getCacheEntry()?.removed ?? [])
	);
	const wasHiddenRef = useRef(hideSatelliteOverlay);

	// When map only turns on mid-drag of an obstruction handle, freeze it: snapshot its
	// exact position into the global store and pin it via dragBoundFunc (the drag session
	// is never stopped, so no re-click is needed). On exit, restore the snapshotted value
	// and remove the pin — the handle sits at its last frozen position until the user moves
	// the mouse, at which point it follows again naturally.
	useEffect(() => {
		const wasHidden = wasHiddenRef.current;
		wasHiddenRef.current = hideSatelliteOverlay;
		if (!isDraggingRef.current) return;
		const node = Konva.DD.node;
		if (!node?.isDragging()) return;

		if (hideSatelliteOverlay && !wasHidden) {
			const frozenPos = node.getAbsolutePosition();
			node.dragBoundFunc(() => frozenPos);
			dispatch(setActiveDragFunctionality(DRAG_FUNCTIONALITY));
			dispatch(setMapOnlyFrozenPoint(frozenPos));
			return;
		}

		if (!hideSatelliteOverlay && wasHidden) {
			if (mapOnlyFrozenPoint) node.setAbsolutePosition(mapOnlyFrozenPoint);
			node.dragBoundFunc(undefined as any);
			dispatch(setActiveDragFunctionality(null));
			dispatch(setMapOnlyFrozenPoint(null));
			node.getLayer()?.batchDraw();
		}
	}, [hideSatelliteOverlay, mapOnlyFrozenPoint, dispatch]);
	const isTouch = typeof window !== 'undefined' && (
		'ontouchstart' in window || navigator.maxTouchPoints > 0
	);
	const isSmallImage = imgHeight <= 601;
	const handleZoomMode = fullScreenEnabled ? 'fullScreen' : 'windowed';
	const currentHandleSize = fullScreenEnabled
		? OBSTRUCTION_HANDLE_SIZE_CONFIG.fullScreen
		: OBSTRUCTION_HANDLE_SIZE_CONFIG.windowed;
	const baseSquareHandleSize = isSmallImage ? currentHandleSize.squareSmall : currentHandleSize.squareDefault;
	const baseCircleHandleRadius = isSmallImage ? currentHandleSize.circleSmall : currentHandleSize.circleDefault;
	const squareHandleSize = getObstructionHandleSize(baseSquareHandleSize, zoomLevel, handleZoomMode);
	const circleHandleRadius = getObstructionHandleSize(baseCircleHandleRadius, zoomLevel, handleZoomMode);
	const zonePadding = EDITING_ZONE_THRESHOLD / zoomLevel;
	const editingZoneHitStroke = (OBSTRUCTION_ADD_POINT_HIT_STROKE / zoomLevel) + (2 * zonePadding);
	const isPolygon = !isCircle && shape === 'POLYGON';
	const isInvalid = invalidObstructionIds.includes(id);
	const invalidStateRef = useRef(isInvalid);
	const dragInvalidRef = useRef(isInvalid);
	const invalidCountRef = useRef(invalidObstructionIds.length);
	const isAddPointMode = obstructionEditPointAction === OBS_EDIT_POINT_ACTION.ADD_POINT;
	const isDeletePointMode = obstructionEditPointAction === OBS_EDIT_POINT_ACTION.DELETE_POINT;
	const isPointEditMode = isAddPointMode || isDeletePointMode;
	const resizeHitStroke = OBSTRUCTION_HANDLE_HIT_STROKE / zoomLevel;
	const resizeHitPadding = resizeHitStroke;
	const deleteHitStroke = (isTouch ? OBSTRUCTION_DELETE_POINT_HIT_SIZE_TOUCH : OBSTRUCTION_DELETE_POINT_HIT_SIZE) / zoomLevel;
	const pointHitStroke = isDeletePointMode ? Math.max(deleteHitStroke, resizeHitStroke) : resizeHitStroke;
	const isLineInvalid = isPolygon && isDraggingRef.current ? dragInvalidRef.current : isInvalid;

	const syncPolygonValidity = useCallback((_options: { showToast?: boolean } = {}) => {
		if (!isPolygon) {
			if (invalidStateRef.current) {
				invalidStateRef.current = false;
				dispatch(setInvalidObstructionState({ id, invalid: false }));
			}
			return false;
		}

		const isSelfIntersecting = isPolygonSelfIntersecting(pointsRef.current);
		if (invalidStateRef.current !== isSelfIntersecting) {
			invalidStateRef.current = isSelfIntersecting;
			dispatch(setInvalidObstructionState({ id, invalid: isSelfIntersecting }));
		}

		const otherInvalidCount = Math.max(0, invalidCountRef.current - (invalidStateRef.current ? 1 : 0));
		const hasAnyInvalid = isSelfIntersecting || otherInvalidCount > 0;
		if (_options.showToast && hasAnyInvalid && !deleteObstructionsMode && !appToast.isActive('invalid-obstruction-error')) {
			appToast.sticky(
				<InvalidFacetEditToast shouldDisable={false} />,
				{
					toastId: 'invalid-obstruction-error',
					className: 'invalid-error-toast',
					containerId: 'tool-toast'
				}
			);
		}

		return isSelfIntersecting;
	}, [dispatch, id, isPolygon, deleteObstructionsMode]);

	const syncPolygonValidityDuringDrag = useCallback((_options: { showToast?: boolean } = {}) => {
		if (!isPolygon) return false;
		const isSelfIntersecting = isPolygonSelfIntersecting(pointsRef.current);
		dragInvalidRef.current = isSelfIntersecting;

		const hasAnyInvalid = isSelfIntersecting || invalidCountRef.current > 0;
		if (_options.showToast && hasAnyInvalid && !deleteObstructionsMode && !appToast.isActive('invalid-obstruction-error')) {
			appToast.sticky(
				<InvalidFacetEditToast shouldDisable={false} />,
				{
					toastId: 'invalid-obstruction-error',
					className: 'invalid-error-toast',
					containerId: 'tool-toast'
				}
			);
		} else if (_options.showToast && !hasAnyInvalid && !deleteObstructionsMode && appToast.isActive('invalid-obstruction-error')) {
			appToast.dismiss({
				id: 'invalid-obstruction-error',
				containerId: 'tool-toast'
			});
		}

		return isSelfIntersecting;
	}, [isPolygon, deleteObstructionsMode]);

	useEffect(() => {
		invalidStateRef.current = isInvalid;
		dragInvalidRef.current = isInvalid;
	}, [id, isInvalid]);
	useEffect(() => {
		invalidCountRef.current = invalidObstructionIds.length;
	}, [invalidObstructionIds.length]);

	useEffect(() => {
		if (isDraggingRef.current) return;
		const prevPoints = pointsRef.current.slice();
		// Reset geometry from parent (undo / external edit)
		pointsRef.current = points.slice();
		prevPointsRef.current = prevPoints;


		if (assessment === 'CIRCLE') {
			circleRef.current = getCircleFromPolygon(pointsRef.current);
		}

		if (detectShape(pointsRef.current) === 'RECTANGLE') {
			rectBoundsRef.current = pointsToBounds(pointsRef.current);
		} else {
			rectBoundsRef.current = null;
		}

		syncPolygonValidity();
		forceUpdate();
	}, [assessment, points, syncPolygonValidity]);

	useEffect(() => {
		const currentKeys = new Set<string>();
		for (let i = 0; i < pointsRef.current.length; i += 2) {
			currentKeys.add(toPointKey(pointsRef.current[i], pointsRef.current[i + 1]));
		}

		const prevPoints = prevPointsRef.current;
		const prevKeys = new Set<string>();
		for (let i = 0; i < prevPoints.length; i += 2) {
			prevKeys.add(toPointKey(prevPoints[i], prevPoints[i + 1]));
		}

		const next = new Set<string>();
		const missingKeys: string[] = [];

		if (highlightedPointKeys.size) {
			highlightedPointKeys.forEach((key) => {
				if (currentKeys.has(key)) {
					next.add(key);
				} else {
					missingKeys.push(key);
				}
			});
			if (missingKeys.length && prevPoints.length === pointsRef.current.length) {
				const prevKeyToIndex = new Map<string, number>();
				for (let i = 0; i < prevPoints.length; i += 2) {
					prevKeyToIndex.set(
						toPointKey(prevPoints[i], prevPoints[i + 1]),
						i
					);
				}
				const currentIndexToKey = new Map<number, string>();
				for (let i = 0; i < pointsRef.current.length; i += 2) {
					currentIndexToKey.set(
						i,
						toPointKey(pointsRef.current[i], pointsRef.current[i + 1])
					);
				}
				missingKeys.forEach((key) => {
					const idx = prevKeyToIndex.get(key);
					if (idx === undefined) return;
					const newKey = currentIndexToKey.get(idx);
					if (newKey) next.add(newKey);
				});
			}
		}

		if (prevPoints.length >= 6 && pointsRef.current.length > prevPoints.length) {
			const removedSet = removedHighlightedKeysRef.current;
			currentKeys.forEach((key) => {
				if (!prevKeys.has(key) && removedSet.has(key)) {
					next.add(key);
					removedSet.delete(key);
				}
			});
			const entry = getCacheEntry();
			if (entry) entry.removed = new Set(removedSet);
		}

		const isSame =
			next.size === highlightedPointKeys.size &&
			Array.from(next).every((key) => highlightedPointKeys.has(key));
		if (!isSame) {
			setHighlightedPointKeys(next);
		}
	}, [points, highlightedPointKeys, pointsHighlightCacheRef]);

	useEffect(() => {
		const entry = getCacheEntry();
		if (!entry) return;
		entry.highlighted = new Set(highlightedPointKeys);
	}, [id, highlightedPointKeys, pointsHighlightCacheRef]);

	const captureBeforeSnapshot = () => {
		if (hasPushedUndoRef.current) return;

		originalSnapshotRef.current = {
			id,
			coordinates: flatToNested(pointsRef.current),
			assessment,
			userMapped,
			area: polygonArea(pointsRef.current),
		};

		hasPushedUndoRef.current = true;
	};

	const vertexIndices = useMemo(() => {
		const out: number[] = [];
		for (let i = 0; i < pointsRef.current.length; i += 2) out.push(i);
		return out;
	}, [pointsRef.current.length]);

	const findClosestPointIndex = (flatPoints: number[], x: number, y: number) => {
		let closestIdx = -1;
		let closestDist = Number.POSITIVE_INFINITY;
		for (let i = 0; i < flatPoints.length; i += 2) {
			const dx = flatPoints[i] - x;
			const dy = flatPoints[i + 1] - y;
			const dist = Math.hypot(dx, dy);
			if (dist < closestDist) {
				closestDist = dist;
				closestIdx = i;
			}
		}
		return closestIdx;
	};

	const isPointInsidePolygon = (point: { x: number; y: number }, polygon: number[][]) => {
		let inside = false;
		for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
			const xi = polygon[i][0];
			const yi = polygon[i][1];
			const xj = polygon[j][0];
			const yj = polygon[j][1];

			const intersect = ((yi > point.y) !== (yj > point.y)) &&
				(point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);

			if (intersect) inside = !inside;
		}
		return inside;
	};

	const findInsertedPointIndex = (
		previous: number[],
		next: number[],
		clickedX: number,
		clickedY: number
	) => {
		if (next.length <= previous.length) return findClosestPointIndex(next, clickedX, clickedY);
		const tol = 1e-6;
		let i = 0;
		let j = 0;
		while (i < previous.length && j < next.length) {
			const sameX = Math.abs(previous[i] - next[j]) <= tol;
			const sameY = Math.abs(previous[i + 1] - next[j + 1]) <= tol;
			if (sameX && sameY) {
				i += 2;
				j += 2;
				continue;
			}
			return j;
		}
		if (j < next.length) return j;
		return findClosestPointIndex(next, clickedX, clickedY);
	};

	const commitEdit = () => {
		const flat = pointsRef.current;

		const afterSnapshot = {
			id,
			coordinates: flatToNested(flat),
			assessment,
			userMapped,
			area: polygonArea(flat),
		};

		dispatch(setEditedObstruction(afterSnapshot));

		if (originalSnapshotRef.current) {
			dispatch(
				pushUndoAction({
					type: 'EDIT',
					obstructionId: id,
					before: originalSnapshotRef.current,
					after: afterSnapshot,
				})
			);
		}

		originalSnapshotRef.current = null;
		hasPushedUndoRef.current = false;
	};

	const handleRadiusDrag = (x: number, y: number) => {
		if (!circleRef.current) return;
		const dx = x - circleRef.current.x;
		const dy = y - circleRef.current.y;
		circleRef.current.radius = Math.max(HANDLE_CIRCLE_RADIUS, Math.sqrt(dx * dx + dy * dy));
		pointsRef.current = circleToPolygon(
			circleRef.current.x,
			circleRef.current.y,
			circleRef.current.radius
		);
		forceUpdate();
	};

	const updatePoint = (index: number, x: number, y: number) => {
		if (!interactive) return;
		const prevKey = toPointKey(pointsRef.current[index], pointsRef.current[index + 1]);
		updatePolygonPoint(pointsRef.current, index, x, y);
		const nextKey = toPointKey(x, y);
		setHighlightedPointKeys((prev) => {
			if (!prev.has(prevKey)) return prev;
			const next = new Set(prev);
			next.delete(prevKey);
			next.add(nextKey);
			return next;
		});
		syncPolygonValidityDuringDrag({ showToast: true });
		forceUpdate();
	};

	const handlePolygonLineClick = (e: Konva.KonvaEventObject<Event>) => {
		const isTouchEvt = typeof TouchEvent !== 'undefined' && e.evt instanceof TouchEvent;
		if (!interactive || !isPolygon) {
			if (isTouchEvt) {
				onTap?.(e);
			} else {
				onClick?.(e as Konva.KonvaEventObject<MouseEvent>);
			}
			return;
		}

		if (isDeletePointMode) {
			const absPointerPos = konvaRef?.current?.getRelativePointerPosition();
			if (!absPointerPos) {
				return;
			}

			let closestIdx = -1;
			let closestDist = Number.POSITIVE_INFINITY;
			for (let i = 0; i < pointsRef.current.length; i += 2) {
				const sx = pointsRef.current[i] * scale.x;
				const sy = pointsRef.current[i + 1] * scale.y;
				const dist = Math.hypot(sx - absPointerPos.x, sy - absPointerPos.y);
				if (dist < closestDist) {
					closestDist = dist;
					closestIdx = i;
				}
			}

			const zoom = Math.max(0.05, zoomLevel);
			const deleteThreshold = OBSTRUCTION_DELETE_POINT_CLICK_THRESHOLD * zoom;
			if (closestIdx >= 0 && closestDist <= deleteThreshold) {
				deletePolygonPoint(closestIdx);
				e.cancelBubble = true;
			}
			return;
		}

		if (!isAddPointMode) {
			if (isTouchEvt) {
				onTap?.(e);
			} else {
				onClick?.(e as Konva.KonvaEventObject<MouseEvent>);
			}
			return;
		}

		const absPointerPos = konvaRef?.current?.getRelativePointerPosition();
		if (!absPointerPos) {
			return;
		}

		const scaledPoints = pointsRef.current.map((p, i) => (
			i % 2 === 0 ? p * scale.x : p * scale.y
		));
		const zoom = Math.max(0.05, zoomLevel);
		const addPointThreshold = OBSTRUCTION_ADD_POINT_CLICK_THRESHOLD * zoom;
		const innerThreshold = OBSTRUCTION_ADD_POINT_INNER_THRESHOLD * zoom;
		const dist = distanceToEdge(absPointerPos, scaledPoints);
		if (dist > addPointThreshold) {
			if (isTouchEvt) {
				onTap?.(e);
			} else {
				onClick?.(e as Konva.KonvaEventObject<MouseEvent>);
			}
			return;
		}

		const clickedX = absPointerPos.x / scale.x;
		const clickedY = absPointerPos.y / scale.y;
		const isInside = isPointInsidePolygon(
			{ x: clickedX, y: clickedY },
			flatToNested(pointsRef.current)
		);
		if (isInside && dist > innerThreshold) {
			return;
		}

		captureBeforeSnapshot();
		const previousPoints = pointsRef.current.slice();
		const newCoords = addNewPoint(absPointerPos, flatToNested(pointsRef.current), scale);
		pointsRef.current = newCoords.flat();
		syncPolygonValidity({ showToast: true });

		const newIndex = findInsertedPointIndex(previousPoints, pointsRef.current, clickedX, clickedY);
		if (newIndex >= 0) {
			const key = toPointKey(pointsRef.current[newIndex], pointsRef.current[newIndex + 1]);
			setHighlightedPointKeys((prev) => {
				const nextSet = new Set(prev);
				nextSet.add(key);
				return nextSet;
			});
		}

		forceUpdate();
		commitEdit();
		e.cancelBubble = true;
	};

	const deletePolygonPoint = (index: number) => {
		if ((pointsRef.current.length / 2) <= 3) {
			appToast.warn('Minimum 3 points are required.', {
				autoClose: 2000,
				position: 'bottom-center',
				containerId: 'tool-toast',
				toastId: 'min-obs-points',
			});
			return;
		}

		captureBeforeSnapshot();
		const removedKey = toPointKey(pointsRef.current[index], pointsRef.current[index + 1]);
		if (highlightedPointKeys.has(removedKey)) {
			removedHighlightedKeysRef.current.add(removedKey);
			const entry = getCacheEntry();
			if (entry) entry.removed = new Set(removedHighlightedKeysRef.current);
		}
		pointsRef.current = pointsRef.current.filter((_, i) => i !== index && i !== index + 1);
		setHighlightedPointKeys((prev) => {
			const nextSet = new Set(prev);
			nextSet.delete(removedKey);
			return nextSet;
		});
		forceUpdate();
		commitEdit();
		syncPolygonValidity({ showToast: true });
	};

	const handleVertexClick = (e: Konva.KonvaEventObject<MouseEvent>, index: number) => {
		if (!interactive || !isPolygon || !isDeletePointMode) {
			onClick?.(e);
			return;
		}

		deletePolygonPoint(index);
		e.cancelBubble = true;
	};

	const handleVertexTap = (e: Konva.KonvaEventObject<Event>, index: number) => {
		if (!interactive || !isPolygon || !isDeletePointMode) {
			onTap?.(e);
			return;
		}

		deletePolygonPoint(index);
		e.cancelBubble = true;
	};

	const handleVertexDragEnd = () => {
		rectDragIndexRef.current = null;
		syncPolygonValidity({ showToast: true });
		commitEdit();
	};

	const handleGroupDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
		const group = e.currentTarget as Konva.Group;
		const isZooming = group.getStage()?.getAttr('isZooming');

		// IMPORTANT: If we canceled the drag or the stage is currently zooming,
		// snap the shape back to 0,0 and ABORT the edit!
		if (group.getAttr('dragCanceled') || isZooming) {
			group.position({ x: 0, y: 0 }); // Hard reset
			group.setAttr('dragCanceled', false); // Clear flag
			isDraggingRef.current = false;
			setCursor(fallbackCursor);
			e.cancelBubble = true;
			return;
		}
		const dx = group.x();
		const dy = group.y();

		if (!dx && !dy) {
			setCursor(fallbackCursor);
			return;
		}
		if (isCircle && circleRef.current) {
			circleRef.current.x += dx;
			circleRef.current.y += dy;

			pointsRef.current = circleToPolygon(
				circleRef.current.x,
				circleRef.current.y,
				circleRef.current.radius
			);
		} else {
			pointsRef.current = pointsRef.current.map((v, i) =>
				i % 2 === 0 ? v + dx : v + dy
			);
		}
		if (shape === 'RECTANGLE') {
			rectBoundsRef.current = pointsToBounds(pointsRef.current);
		}
		group.position({ x: 0, y: 0 });
		forceUpdate();
		commitEdit();
		isDraggingRef.current = false;
		setCursor(fallbackCursor);
	};

	const handleGroupDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
		const group = e.target as Konva.Group;
		group.setAttr('dragCanceled', false);

		const evt = e.evt as { touches?: { length: number } } | undefined;
		const isMultiTouch = !!(evt && evt.touches && evt.touches.length > 1);
		const isZooming = group.getStage()?.getAttr('isZooming');

		if (isZooming || isMultiTouch) {
			group.setAttr('dragCanceled', true);
			group.stopDrag();
			return;
		}
		setCursor('move');
		isDraggingRef.current = true;
		captureBeforeSnapshot();
	};

	const handleGroupDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
		const group = e.currentTarget as Konva.Group;
		const evt = e.evt as { touches?: { length: number } } | undefined;
		const isMultiTouch = !!(evt && evt.touches && evt.touches.length > 1);
		const isZooming = group.getStage()?.getAttr('isZooming');

		if (isZooming || isMultiTouch) {
			group.setAttr('dragCanceled', true);
			group.stopDrag();
			isDraggingRef.current = false;
			setCursor(fallbackCursor);
		}
	};
	// 1. Identify which Index (0-3) is being dragged
	const handleRectDragStart = (index: number) => {
		captureBeforeSnapshot();
		rectDragIndexRef.current = index;
	};

	// 2. Update points directly, preserving order, ignoring bounds normalization
	const handleRectDragMove = (index: number, newX: number, newY: number) => {
		if (rectDragIndexRef.current === null) return;

		const p = pointsRef.current;
		// The opposite corner index in a 4-point rect is always (i + 2) % 4
		const oppIndex = (index + 2) % 4;

		const oppX = p[oppIndex * 2];
		const oppY = p[oppIndex * 2 + 1];

		// Update the dragged point
		p[index * 2] = newX;
		p[index * 2 + 1] = newY;

		// Update the two neighbors to maintain 90 degree axis alignment
		// Neighbor 1: (index + 1)
		// Neighbor 3: (index + 3)
		// One neighbor shares X with Dragged and Y with Opposite
		// The other shares Y with Dragged and X with Opposite
    
		// We can infer this simply: the rectangle is formed by permutations of {newX, oppX} and {newY, oppY}
		// We just need to know which neighbor is which.
    
		// Neighbor 1 index
		const n1 = (index + 1) % 4;
		// Neighbor 3 index
		const n3 = (index + 3) % 4;

		// Note: This assumes points are ordered [TL, TR, BR, BL] or similar winding.
		// Even if winding is different, one neighbor matches X, one matches Y.
    
		// Let's look at the current state of neighbor 1 to decide its role
		// If n1 currently shares X with `index`, keep it sharing X (update X to newX)
		// If n1 currently shares Y with `index`, keep it sharing Y (update Y to newY)
    
		// However, a simpler geometric reconstruction is robust:
		// If we assume standard winding, Neighbor 1 usually shares Y (or X depending on start).
		// Let's force alignment based on the specific points array structure.
    
		// Standard approach:
		// P_neighbor_1.x = P_opposite.x OR P_dragged.x
		// To solve this dynamically without assuming winding:
		// P_n1 should change ONLY one coordinate to match P_dragged, and keep the other matching P_opp.
    
		if (Math.abs(p[n1 * 2] - p[index * 2]) < Math.abs(p[n1 * 2 + 1] - p[index * 2 + 1])) {
			// P_n1 was closer in X to Dragged (vertical neighbor?) -> Update X
			// This check is risky during drag.
		}
    
		// Robust approach: 
		// Just force the rect logic. 
		// P_dragged = (newX, newY)
		// P_opp = (oppX, oppY)
		// P_adj1 = (newX, oppY)
		// P_adj2 = (oppX, newY)
    
		// We just need to assign adj1 and adj2 to the correct indices (n1 vs n3)
		// We can check which index had the matching X or Y previously.
		// But since we are editing `pointsRef` in place, we can just check the PREVIOUS value? 
		// No, that's gone.
    
		// Let's trust the array structure:
		// 0: x0, y0
		// 1: x1, y0  (Shares Y with 0)
		// 2: x1, y1
		// 3: x0, y1  (Shares X with 0)
		// If drag 0:
		// 1 should update Y to newY (keep x1)
		// 3 should update X to newX (keep y1)
    
		// The generic rule: 
		// Neighbor `i+1` shares one axis, Neighbor `i-1` shares the other.
		// If points were generated by boundsToPoints, they are: TL, TR, BR, BL.
		// 0: TL, 1: TR. Share Y.
		// 1: TR, 2: BR. Share X.
    
		// So for standard layout:
		// (index + 1) shares Y? Not always.
    
		// Let's simply update the orthogonal projections:
		// If index is even (0 or 2), odd neighbors are (x_drag, y_opp) and (x_opp, y_drag).
		// If index is odd (1 or 3), even neighbors are ...
    
		// Actually, simple check:
		// p[n1 * 2]     should be newX if it was oldX, or oppX if it was oppX.
		// Let's just set them based on index parity if we assume standard generation.
		// But to be 100% safe against any winding:
    
		// Check which coordinate of n1 matched the OLD opposite.
		// If n1.x == opp.x, then n1.x MUST remain opp.x. So n1.y becomes newY.
		// This implies n3.x becomes newX and n3.y remains opp.y.
    
		// Since we can't easily check "== opp.x" due to floats/dragging:
		// Let's just update all of them based on a standard rect reconstruction:
		// We treat the rectangle as an axis aligned box defined by P_dragged and P_opp.
		// We recreate the intermediate points.
    
		// We update n1: Take X from n1 (keep it), take Y from Dragged?
		// NO. If we are dragging, we change the bounds.
    
		// Correct Logic:
		// n1 takes X from either Dragged or Opp.
		// n3 takes the other X.
		// We determine which is which by checking `pointsRef` before modification? 
		// Too complex.
    
		// Simplest fix that works for 99% of `boundsToPoints` outputs:
		// Assume n1 (index+1) takes Y from dragged, X from opposite?
		// Or just look at the code:
		// pointsRef.current is the source of truth.
		// Let's just update n1.x to be p[index*2] (align X) or p[oppIndex*2] (align X to opp).
    
		// Let's try: Update n1 to share X with dragged, n3 to share Y with dragged.
		// If that twists the shape (bowtie), swap them.
		// Actually, for a Rect, it doesn't matter if indices rotate as long as visual is correct?
		// No, we want to preserve indices.
    
		// Let's look at `boundsToPoints` output order usually:
		// minX, minY (TL) -> maxX, minY (TR) -> maxX, maxY (BR) -> minX, maxY (BL)
		// 0 -> 1: share Y.
		// 0 -> 3: share X.
    
		// So: 
		// Neighbor (index + 1) shares Y with index? No, 0&1 share Y. 1&2 share X.
		// Parity check:
		// If index is 0 or 2: (TL or BR)
		//    Neighbor (i+1) shares Y. (0->1 share Y, 2->3 share Y).
		//    Neighbor (i-1) shares X. (0->3 share X, 2->1 share X).
		// If index is 1 or 3: (TR or BL)
		//    Neighbor (i+1) shares X. (1->2 share X, 3->0 share X).
		//    Neighbor (i-1) shares Y. (1->0 share Y, 3->2 share Y).

		const isEven = index % 2 === 0;

		if (isEven) {
			// Dragging 0 or 2
			// n1 (0->1 or 2->3) shares Y. So update its Y to newY. Keep its X (which matches OppX).
			p[n1 * 2 + 1] = newY;
			p[n1 * 2] = oppX; // Explicitly snap to OppX to prevent drift
        
			// n3 (0->3 or 2->1) shares X. So update its X to newX. Keep its Y (which matches OppY).
			p[n3 * 2] = newX;
			p[n3 * 2 + 1] = oppY;
		} else {
			// Dragging 1 or 3
			// n1 (1->2 or 3->0) shares X. Update X to newX.
			p[n1 * 2] = newX;
			p[n1 * 2 + 1] = oppY;
        
			// n3 (1->0 or 3->2) shares Y. Update Y to newY.
			p[n3 * 2 + 1] = newY;
			p[n3 * 2] = oppX;
		}

		// Now update the visual bounds for the <Rect> component
		rectBoundsRef.current = pointsToBounds(p);
    
		forceUpdate();
	};

	const handleRectDragEnd = () => {
		rectDragIndexRef.current = null;
		commitEdit();
	};

	const setCursor = (cursor: string) => {
		const container = konvaRef?.current?.container();
		if (!container) return;
		if (isDraggingRef.current && cursor !== 'move') return;
		container.style.cursor = cursor;
	};

	const handleEnter = () => {
		if (interactive) setCursor('pointer');
	};

	const handleLeave = () => {
		if (interactive) setCursor('default');
	};

	const setDragAllowed = (e: Konva.KonvaEventObject<Event>) => {
		const target = e.target as Konva.Node;
		const canDrag = canStartDragFromNode(
			target,
			RAW_OBSTRUCTION_BODY_NODE_NAMES,
			RAW_OBSTRUCTION_DRAG_BLOCKER_NODE_NAMES
		);
		(e.currentTarget as Konva.Group).setAttr('dragAllowed', canDrag);
	};

	const clearDragAllowed = (e: Konva.KonvaEventObject<Event>) => {
		(e.currentTarget as Konva.Group).setAttr('dragAllowed', false);
	};

	return (
		<Group
			id={id}
			draggable={interactive}
			dragDistance={isTouch && isPointEditMode ? 8 : 0}
			onMouseDown={setDragAllowed}
			onTouchStart={setDragAllowed}
			onMouseUp={clearDragAllowed}
			onTouchEnd={clearDragAllowed}
			onClick={(e) => { e.cancelBubble = true; }}
			onTap={(e) => { e.cancelBubble = true; }}
			dragBoundFunc={function(this: Konva.Node, pos: Konva.Vector2d) {
				const isZooming = this.getStage()?.getAttr('isZooming');

				let isMultiTouch = false;
				if (typeof window !== 'undefined' && window.event) {
					const winEvt = window.event as { touches?: { length: number } } | undefined;
					isMultiTouch = !!(winEvt && winEvt.touches && winEvt.touches.length > 1);
				}

				// Freeze shape in place if a pinch or zoom is detected
				if (isZooming || isMultiTouch || this.getAttr('dragCanceled')) {
					if (!this.getAttr('dragCanceled')) {
						this.setAttr('dragCanceled', true);
					}
					return this.getAbsolutePosition();
				}
				return pos;
			}}
			onDragStart={(e) => {
				const group = e.currentTarget as Konva.Group;
				if (group.getAttr('dragAllowed') !== true) {
					group.stopDrag();
					return;
				}
				handleGroupDragStart(e);
				e.cancelBubble = true;
				e.evt?.preventDefault();
			}}
			onDragMove={(e) => {
				handleGroupDragMove(e);
				e.cancelBubble = true;
			}}
			onDragEnd={(e) => {
				clearDragAllowed(e as Konva.KonvaEventObject<Event>);
				handleGroupDragEnd(e);
				e.cancelBubble = true;
				e.evt?.preventDefault();
			}}>
			
			{/* ==================== EDITING ZONE (INVISIBLE) ==================== */}
			{/* Larger invisible zone for add/delete points when selected */}
			{interactive && isPolygon && isPointEditMode && (
				<Line
					name="raw-obs-edit-zone"
					points={pointsRef.current}
					closed
					fill="rgba(0,0,0,0.01)"
					stroke="transparent"
					listening={true}
					hitStrokeWidth={editingZoneHitStroke}
					onClick={handlePolygonLineClick}
					onTap={handlePolygonLineClick}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				/>
			)}

			{/* ==================== VISIBLE SHAPES ==================== */}
			{isCircle && circleRef.current ? (
				<Circle
					id={id}
					name="raw-obs-circle"
					x={circleRef.current.x}
					y={circleRef.current.y}
					radius={circleRef.current.radius}
					stroke= "red"
					strokeWidth={1}
					fill={OBSTRUCTION_DEFAULT_FILL}
					strokeScaleEnabled={false}
					listening={true}
					onClick={onClick}
					onTap={onTap}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				/>
			) : shape === 'RECTANGLE' && rectBoundsRef.current ? (
				<Rect
					id={id}
					name="raw-obs-rect"
					x={rectBoundsRef.current.minX}
					y={rectBoundsRef.current.minY}
					width={rectBoundsRef.current.maxX - rectBoundsRef.current.minX}
					height={rectBoundsRef.current.maxY - rectBoundsRef.current.minY}
					stroke= "red"
					strokeWidth={1}
					fill={OBSTRUCTION_DEFAULT_FILL}
					strokeScaleEnabled={false}
					listening={true}
					onClick={onClick}
					onTap={onTap}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				/>
			) : (
				<Line
					id={id}
					name="raw-obs-line"
					points={pointsRef.current}
					closed
					stroke= "red"
					strokeWidth={1}
					fill={isLineInvalid ? OBSTRUCTION_INVALID_FILL : OBSTRUCTION_DEFAULT_FILL}
					strokeScaleEnabled={false}
					hitStrokeWidth={interactive ? 0 : (OBSTRUCTION_ADD_POINT_HIT_STROKE / zoomLevel)}
					listening={true}
					onClick={handlePolygonLineClick}
					onTap={handlePolygonLineClick}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				/>
			)}

			{readOnly ? null : (isCircle && circleRef.current ? (
				buildSquareHandlesForCircle(
					id,
					{ x: circleRef.current.x, y: circleRef.current.y },
					circleRef.current.radius,
					handleRadiusDrag,
					commitEdit,
					captureBeforeSnapshot,
					isDraggingRef,
					squareHandleSize,
					(1 / zoomLevel),
					pointHitStroke,
					resizeHitPadding,
					interactive,
					onClick, 
					onTap,   
					handleEnter,
					handleLeave,
				)
			) : (
				shape === 'RECTANGLE' && rectBoundsRef.current
					? buildSquareHandlesRectangle(
						id, 
						pointsRef.current,
						handleRectDragStart,
						handleRectDragMove,
						handleRectDragEnd,
						isDraggingRef,
						squareHandleSize,
						(1 / zoomLevel),
						pointHitStroke,
						resizeHitPadding,
						interactive,
						onClick, 
						onTap,   
						handleEnter,
						handleLeave,
					)
					: buildCircleHandlesForPolygon(
						id, 
						pointsRef.current,
						vertexIndices,
						updatePoint,
						handleVertexDragEnd,
						captureBeforeSnapshot,
						isDraggingRef,
						circleHandleRadius,
						(1 / zoomLevel),
						pointHitStroke,
						resizeHitPadding,
						interactive,
						highlightedPointKeys,
						handleVertexClick,
						handleVertexTap,
						onClick, 
						onTap,   
						handleEnter,
						handleLeave,
					)
			))}
		</Group>
	);
}
